import {EventsMgr} from "/@lib@/commons/events.js";
import {DOM} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {REG} from "/@lib@/commons/registry.js";
import {BaseElement, OSkinableInit} from "/@back@/commons/basis.js";

/**
 * Widget pour retailler les box flex adjacents.
 * Le parent DOIT être display:flex.
 * Les box adjacents qui peuvent être retaillés DOIVENT posséder un attribut c-resizable=''.
 *
 * Par défaut l'implémentation gère automatiquement et dynamiquement l'orientation.
 * Pour améliorer l'affichage (débord dans le cross-axis), il est possible d'ajouter un
 * attribut c-orient="column" c-orient="row" qui fixe à priori l'orientation lorsqu'elle est connue.
 *
 * Le resizer peut rechercher des noeuds c-resizable='' parmi les oncles du resizer en spécifiant
 *  un attribut find-upto positif coorespondnat au nombre d'ancêtres à remonter.
 */
export class Resizer extends BaseElement {

	protected _listeners: EventsMgr<IResizerEvents>;

	get listeners(): EventsMgr<IResizerEvents> {return this._listeners || (this._listeners = new EventsMgr())}

	get findUpTo(): number {return parseInt(this.getAttribute('find-upto'), 10) || 0}

	set findUpTo(levels: number) {this.setAttribute('find-upto', levels.toString())}

	get disabled() {return this.hasAttribute("disabled")}

	set disabled(v: boolean) {DOM.setAttrBool(this, 'disabled', v)}

	protected _initialize(init: OSkinableInit) {
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);
		sr.appendChild(document.createElement('slot'));
		this.onpointerdown = this.onPointerDown;
	}

	protected _horizontal: boolean;
	protected _startOffset: number;
	protected _prevResizables: (ResizeNodeH | ResizeNodeV)[];
	protected _nextResizables: (ResizeNodeH | ResizeNodeV)[];

	/**
	 * Pour étendre la zone de captation du resizing, cette méthode peut aussi être appelée par la
	 * détection de l'event "pointerdown" sur d'autres éléments qui lui transfèrent simplement l'evt :
	 *
	 * otherElt.onpointerdown = function (this: HTMLElement, ev: PointerEvent) {
	 * 		const resizer = this.previousElementSibling as Resizer;
	 *		resizer.onPointerDown(ev);
	 *	}
	 */
	onPointerDown(ev: PointerEvent) {
		//console.log("onDragstart:::", ev);
		if (this.disabled) return;
		ev.stopImmediatePropagation();
		ev.preventDefault();
		let parent = DOMSH.getParentElt(this);
		let parentStyles = window.getComputedStyle(parent);
		while (parentStyles.display !== 'flex') { //on saute les display:contents en particulier (slot...)
			parent = DOMSH.getParentElt(parent);
			if (!parent) return;
			parentStyles = window.getComputedStyle(parent);
		}
		this._horizontal = parentStyles.flexDirection.startsWith('row');
		if (this._listeners) this._listeners.emit('resizeStart');
		this._prevResizables = [];
		this._nextResizables = [];
		let findUpTo = this.findUpTo;
		let from = this as Node;
		const isRtl = window.getComputedStyle(this).direction === 'rtl';
		while (findUpTo >= 0) {
			let prevs: (ResizeNodeH | ResizeNodeV)[];
			let nexts: (ResizeNodeH | ResizeNodeV)[];
			if (parentStyles.flexDirection.endsWith('reverse')) {
				prevs = (isRtl && this._horizontal) ? this._prevResizables : this._nextResizables;
				nexts = (isRtl && this._horizontal) ? this._nextResizables : this._prevResizables;
			} else {
				prevs = (isRtl && this._horizontal) ? this._nextResizables : this._prevResizables;
				nexts = (isRtl && this._horizontal) ? this._prevResizables : this._nextResizables;
			}
			for (let prev = DOMSH.findFlatPrevSibling(from, isResizable); prev; prev = DOMSH.findFlatPrevSibling(prev, isResizable)) {
				prevs.push((this._horizontal ? new ResizeNodeH(prev) : new ResizeNodeV(prev)).initOriginalSizes());
			}
			for (let next = DOMSH.findFlatNextSibling(from, isResizable); next; next = DOMSH.findFlatNextSibling(next, isResizable)) {
				nexts.push((this._horizontal ? new ResizeNodeH(next) : new ResizeNodeV(next)).initOriginalSizes());
			}
			do {
				from = parent;
				parent = DOMSH.getParentElt(from);
				parentStyles = window.getComputedStyle(parent);
				findUpTo--;
			} while (parent && findUpTo >= 0 && !parentStyles.flexDirection.startsWith(this._horizontal ? 'row' : 'column'));
		}
		for (const prev of this._prevResizables) prev.initWriteMinSize();
		for (const next of this._nextResizables) next.initWriteMinSize();
		for (const prev of this._prevResizables) prev.initReadMinSize();
		for (const next of this._nextResizables) next.initReadMinSize();
		//Pas de calcul des MAX-size car si un container contient un scroll sa position est alors perdue.
		//XXX A rendre configurable si besoin d'évaluer ce max-size via @c-resizable='toMax' par exemple.
		// for (const prev of this._prevResizables) prev.initWriteMaxSize();
		// for (const next of this._nextResizables) next.initWriteMaxSize();
		// for (const prev of this._prevResizables) prev.initReadMaxSize();
		// for (const next of this._nextResizables) next.initReadMaxSize();
		for (const prev of this._prevResizables) prev.initRevertToOriginal();
		for (const next of this._nextResizables) next.initRevertToOriginal();
		this._startOffset = this._horizontal ? ev.clientX : ev.clientY;

		if (!this._onDrag) this._onDrag = this.onDrag.bind(this);
		if (!this._onDragend) this._onDragend = this.onDragend.bind(this);
		document.documentElement.addEventListener('pointermove', this._onDrag, true);
		document.documentElement.addEventListener('pointerup', this._onDragend, true);
		if (this._listeners) this._listeners.emit('resizing');
	}

	protected _onDrag: (ev: PointerEvent) => boolean;
	protected _onDragend: (ev: PointerEvent) => boolean;

	protected onDrag(ev: PointerEvent) {
		if (ev.buttons === 0) {
			//Plus de buttons enfoncés, on sort.
			this.onDragend(ev);
			return;
		}
		if (!this._prevResizables) return;
		ev.preventDefault();
		ev.stopImmediatePropagation();
		const offset = this._horizontal ? ev.clientX : ev.clientY;
		if (offset === 0) return;//bug chrome63 : au drop un event 'drag' avec tout à 0 est dispatché.
		let delta = offset - this._startOffset;
		if (delta < 0) {
			//on commence par les précendents qui sont réduits
			for (const resize of this._prevResizables) delta = resize.tryResize(delta);
			delta = this._startOffset - offset /* on abondonne ce qu'on n'a pas réussit à réduire. */ + delta;
			for (const resize of this._nextResizables) delta = resize.tryResize(delta);
			if (delta !== 0) {
				//Le maxSize a bloqué, on abondonne ce qu'on n'a pas réussit à augmenter.
				delta = offset - this._startOffset + delta;
				for (const resize of this._prevResizables) delta = resize.tryResize(delta);
			}
		} else {
			//on commence par les suivants qui sont réduits
			delta = -delta;
			for (const resize of this._nextResizables) delta = resize.tryResize(delta);
			delta = offset - this._startOffset /* on abondonne ce qu'on n'a pas réussit à réduire. */ + delta;
			for (const resize of this._prevResizables) delta = resize.tryResize(delta);
			if (delta !== 0) {
				//Le maxSize a bloqué, on abondonne ce qu'on n'a pas réussit à augmenter.
				delta = this._startOffset - offset + delta;
				for (const resize of this._nextResizables) delta = resize.tryResize(delta);
			}
		}
		this.classList.add("resizing");
		if (this._listeners) this._listeners.emit('resizing');
	}

	protected onDragend(ev: PointerEvent) {
		//console.log("onDragend:::", ev);
		document.documentElement.removeEventListener('pointermove', this._onDrag, true);
		document.documentElement.removeEventListener('pointerup', this._onDragend, true);
		if (!this._prevResizables) return;
		ev.preventDefault();
		ev.stopImmediatePropagation();
		this._prevResizables = null;
		this._nextResizables = null;
		this.classList.remove("resizing");
		if (this._listeners) this._listeners.emit('resizeEnd');
	}

}

/** Events émis par c-resizer. */
export type IResizerEvents = {
	resizeStart: () => void
	resizing: () => void
	resizeEnd: () => void
}

class ResizeNodeH {

	originalFlex: string;
	originalSize: number;
	minSize: number;
	maxSize: number;

	constructor(public elt: HTMLElement) {}

	initOriginalSizes() {
		this.originalSize = this.elt.offsetWidth;
		this.originalFlex = window.getComputedStyle(this.elt).flex;
		return this;
	}

	initWriteMinSize() {
		this.elt.style.flex = "0 0 0";
	}

	initReadMinSize() {
		this.minSize = this.elt.offsetWidth;
	}

	initWriteMaxSize() {
		this.elt.style.flex = "0 0 10000px";
	}

	initReadMaxSize() {
		this.maxSize = this.elt.offsetWidth;
	}

	initRevertToOriginal() {
		this.elt.style.flex = this.originalFlex || "1 1 auto";
		this.elt.style.flexBasis = this.originalSize + 'px';
	}

	/**
	 * Tente de modifier l'original size par ce delta.
	 * Retourne le delta qui n'a pas été pris en compte.
	 */
	tryResize(delta: number): number {
		const newSize = this.originalSize + delta;
		if (newSize < this.minSize) {
			DOM.setStyle(this.elt, 'flex-basis', this.minSize + 'px');
			return newSize - this.minSize;
		}
		if (newSize > this.maxSize) {
			DOM.setStyle(this.elt, 'flex-basis', this.maxSize + 'px');
			return newSize - this.maxSize;
		}
		DOM.setStyle(this.elt, 'flex-basis', newSize + 'px');
		return 0;
	}
}

class ResizeNodeV extends ResizeNodeH {

	initOriginalSizes() {
		this.originalSize = this.elt.offsetHeight;
		this.originalFlex = window.getComputedStyle(this.elt).flex;
		return this;
	}

	initReadMinSize() {
		this.minSize = this.elt.offsetHeight;
	}

	initReadMaxSize() {
		this.maxSize = this.elt.offsetHeight;
	}
}

function isResizable(n: Node): n is HTMLElement {return (n instanceof HTMLElement) && n.hasAttribute('c-resizable') && !n.hidden}

REG.reg.registerSkin('c-resizer', 1, /* language=CSS */ `
	:host {
		flex: none;
		display: flex;
		margin: -2px;
		padding: 2px;
		z-index: 1;
		background: var(--resizer-bgcolor, var(--border-color));
		background-clip: content-box;
		touch-action: none;
	}

	:host([c-orient="row"]) {
		margin-top: 0 !important;
		margin-bottom: 0 !important;
		padding-top: 0 !important;
		padding-bottom: 0 !important;
		flex-direction: row;
	}

	:host([c-orient="row"]:not([disabled])) {
		cursor: e-resize;
	}

	:host([c-orient="column"]) {
		margin-inline-start: 0 !important;
		margin-inline-end: 0 !important;
		padding-inline-start: 0 !important;
		padding-inline-end: 0 !important;
		flex-direction: column;
	}

	:host([c-orient="column"]:not([disabled])) {
		cursor: n-resize;
	}

	:host(:hover:not([disabled])) {
		background-clip: border-box;
		background-color: var(--resizer-hover-bgcolor, #204b8877);
	}

	:host(.resizing) {
		background-clip: content-box !important;
		margin: -100px;
		padding: 100px;
	}

	/* évite l'affichage de scrollbars si resizers au bord (margin négatif) */
	:host([disabled]) {
		margin: 0;
		padding: 0;
	}
`);

customElements.define('c-resizer', Resizer);


export class Collapser extends BaseElement {

	get panelPos(): 'prev' | 'next' {return this.getAttribute('panel-pos') as any}

	get orient(): 'row' | 'column' {return this.getAttribute('c-orient') as any}

	get panel(): Element {
		switch (this.panelPos) {
		case "prev":
			return this.parentElement?.previousElementSibling;
		case "next":
			return this.parentElement?.nextElementSibling;
		}
		return null;
	}

	get closed(): boolean {return this.panel?.hasAttribute("hidden") || false}

	protected _initialize(init: OSkinableInit) {
		this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);
		this.onclick = this.onClick;
	}

	show() {
		const panel = this.panel;
		if (panel && DOM.setHidden(panel, false)) {
			if (this.parentElement instanceof Resizer) this.parentElement.disabled = false;
			this.refresh();
		}
	}

	hide() {
		const panel = this.panel;
		if (panel && DOM.setHidden(panel, true)) {
			if (this.parentElement instanceof Resizer) this.parentElement.disabled = true;
			this.refresh();
		}
	}

	onClick() {
		const panel = this.panel;
		if (panel) {
			if (panel.hasAttribute("hidden")) this.show();
			else this.hide();
		}
	}

	protected _refresh() {
		const styles = window.getComputedStyle(this);
		const reverse = styles.flexDirection.endsWith("-reverse");
		if (this.orient === "row") {
			const left = (styles.direction === 'ltr') === (this.panelPos === "prev");
			this.setAttribute("c-state", (left !== reverse ? 'left' : 'right') + (this.closed ? "Closed" : "Opened"));
		} else {
			const top = (this.panelPos === "prev");
			this.setAttribute("c-state", (top !== reverse ? 'top' : 'bottom') + (this.closed ? "Closed" : "Opened"));
		}
	}
}

/** Stylage adapté à des resizer d'au moins 6px d'épaisseur. */
REG.reg.registerSkin('c-collapser', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		cursor: pointer;
		background-size: 6px;
		background-position: center;
	}

	:host([c-orient="row"]) {
		background-repeat: repeat-y;
		height: 35px;
		align-self: center;
	}

	:host([c-orient="column"]) {
		background-repeat: repeat-x;
		height: 35px;
		justify-self: center;
	}

	:host(:hover) {
		background-color: var(--pressed-bgcolor);
		outline: 1px dotted var(--border-color);
	}

	:host([c-state=leftOpened]) {
		background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 3 6" fill="%23b89a62"><path d="M 3,0 1.13,3 3,6 0,3 Z"/></svg>');
	}

	:host([c-state=leftClosed]) {
		background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 3 6" fill="%23b89a62"><path d="M 0,0 1.87,3 0,6 3,3 Z"/></svg>');
	}

	:host([c-state=righttOpened]) {
		background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 3 6" fill="%23b89a62"><path d="M 0,0 1.87,3 0,6 3,3 Z"/></svg>');
	}

	:host([c-state=rightClosed]) {
		background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 3 6" fill="%23b89a62"><path d="M 3,0 1.13,3 3,6 0,3 Z"/></svg>');
	}

	:host([c-state=topOpened]) {
		background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 6 3" fill="%23b89a62"><path d="M 0,3 3,1.13 6,3 3,0 Z"/></svg>');
	}

	:host([c-state=topClosed]) {
		background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 6 3" fill="%23b89a62"><path d="M 0,0 3,1.87 6,0 3,3 Z"/></svg>');
	}

	:host([c-state=bottomOpened]) {
		background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 6 3" fill="%23b89a62"><path d="M 0,0 3,1.87 6,0 3,3 Z"/></svg>');
	}

	:host([c-state=bottomClosed]) {
		background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 6 3" fill="%23b89a62"><path d="M 0,3 3,1.13 6,3 3,0 Z"/></svg>');
	}
`);

// /** Stylage petit bouton qui déborde pour des resizers très étroits. */
// REG.reg.registerSkin('c-collapser/smallbutton', 1, /* language=CSS */ `
// 	:host {
// 		display: flex;
// 		cursor: pointer;
// 		align-self: start;
// 		background-size: contain;
// 		background-repeat: no-repeat;
// 		background-position: center;
// 		border-radius: .3em;
// 	}
//
// 	:host(:hover) {
// 		background-color: var(--pressed-bgcolor);
// 		outline: 1px dotted var(--border-color);
// 	}
//
// 	:host([c-state=leftOpened]) {
// 		background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 6 6" fill="%23b89a62"><path d="M 3,0 1.13,3 3,6 0,3 Z"/><path d="M 6,0 4.13,3 6,6 3,3 Z"/></svg>');
// 		padding: .6em .4em;
// 		margin: 0 -.4em;
// 	}
//
// 	:host([c-state=leftClosed]) {
// 		background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 6 6" fill="%23b89a62"><path d="M 0,0 1.87,3 0,6 3,3 Z"/><path d="M 3,0 4.87,3 3,6 6,3 Z"/></svg>');
// 		padding: .6em .4em;
// 		margin: 0 -.6em 0 -.2em;
// 	}
//
// 	:host([c-state=righttOpened]) {
// 		background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 6 6" fill="%23b89a62"><path d="M 0,0 1.87,3 0,6 3,3 Z"/><path d="M 3,0 4.87,3 3,6 6,3 Z"/></svg>');
// 		padding: .6em .4em;
// 		margin: 0 -.4em;
// 	}
//
// 	:host([c-state=rightClosed]) {
// 		background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 6 6" fill="%23b89a62"><path d="M 3,0 1.13,3 3,6 0,3 Z"/><path d="M 6,0 4.13,3 6,6 3,3 Z"/></svg>');
// 		padding: .6em .4em;
// 		margin: 0 -.2em 0 -.6em;
// 	}
//
// 	:host([c-state=topOpened]) {
// 		background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 6 6" fill="%23b89a62"><path d="M 0,3 3,1.13 6,3 3,0 Z"/><path d="M 0,6 3,4.13 6,6 3,3 Z"/></svg>');
// 		padding: .4em .6em;
// 		margin: -.4em 0;
// 	}
//
// 	:host([c-state=topClosed]) {
// 		background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 6 6" fill="%23b89a62"><path d="M 0,0 3,1.87 6,0 3,3 Z"/><path d="M 0,3 3,4.87 6,3 3,6 Z"/></svg>');
// 		padding: .4em .6em;
// 		margin: -.6em 0 -.2em 0;
// 	}
//
// 	:host([c-state=bottomOpened]) {
// 		background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 6 6" fill="%23b89a62"><path d="M 0,0 3,1.87 6,0 3,3 Z"/><path d="M 0,3 3,4.87 6,3 3,6 Z"/></svg>');
// 		padding: .4em .6em;
// 		margin: -.4em 0;
// 	}
//
// 	:host([c-state=bottomClosed]) {
// 		background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 6 6" fill="%23b89a62"><path d="M 0,3 3,1.13 6,3 3,0 Z"/><path d="M 0,6 3,4.13 6,6 3,3 Z"/></svg>');
// 		padding: .4em .6em;
// 		margin: -.2em 0 -.6em 0;
// 	}
// `);

customElements.define('c-collapser', Collapser);